/* eslint-disable react/jsx-props-no-spreading */
import React, { Component } from 'react';
import { func, number, objectOf, string } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Icon from 'yoda-core-components/lib/components/Icon';
import Error from 'yoda-core-components/lib/assets/svg/Error.svg';
import Input from 'yoda-core-components/lib/components/Input/Input';
import AnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import classNames from 'classnames/bind';
import * as styles from './CustomPriceRange.css';
import { updateUrl, getURLParameterBykey } from '../../../utils';
import { rangeIdentifier } from '../../../configurations/config';

const cx = classNames.bind(styles);

// istanbul ignore next
export class CustomPriceRange extends Component {
    static defaultProps = {
        facetId: null,
        noProducts: '',
        priceRangeInputSize: null,
    };

    static propTypes = {
        actions: objectOf(func).isRequired,
        maxPrice: number.isRequired,
        minPrice: number.isRequired,
        requestUrl: string.isRequired,
        facetId: number,
        noProducts: string,
        priceRangeInputSize: number,
    };

    constructor(props) {
        super(props);
        this.isMinEntered = false;
        this.isMaxEntered = false;
        this.isDiscountRange = props.facetId === 44682;
        this.myRef = React.createRef();
        this.state = {
            errorMsg: '',
            maxMinErrorMsg: '',
            priceError: false,
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { facetId, noProducts } = nextProps;

        // istanbul ignore next
        if (noProducts.length > 0) {
            const { actions } = this.props;
            const { errorMsg, priceError } = this.state;
            // TODO: Get facet specific error message instead of global error message.
            const currentFilterApplied =
                facetId === 44682 ? getURLParameterBykey('Dr') : getURLParameterBykey('Nf');
            const currentFacet = rangeIdentifier[facetId];
            const selectedRangeFilter = new RegExp(`^${currentFacet.identifier}`, 'g');

            if (!errorMsg && !priceError && selectedRangeFilter.test(currentFilterApplied)) {
                actions.triggerFormError([
                    {
                        errorDescription: noProducts,
                    },
                ]);

                this.setState({
                    errorMsg: noProducts,
                    priceError: true,
                });
            }
        } else {
            this.setState({
                errorMsg: '',
                priceError: false,
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { noProducts } = this.props;
        const { maxMinErrorMsg, priceError } = this.state;
        const hasNoProducts = noProducts.length === nextProps.noProducts.length;
        const isPriceError = priceError !== nextState.priceError;
        const hasErrorMsg = nextState.errorMsg.length > 0;
        const hasMaxMinErrorMsg = nextState.maxMinErrorMsg.length > 0;
        let retract = true;

        if (hasNoProducts) retract = false;

        if (isPriceError || hasErrorMsg || hasMaxMinErrorMsg) retract = true;

        if (nextState.maxMinErrorMsg.length !== maxMinErrorMsg.length) return true;

        return retract;
    }

    componentDidUpdate() {
        const { maxMinErrorMsg, priceError } = this.state;

        if (priceError || maxMinErrorMsg.length > 0) this.scrollToMyRef();
    }

    onChange = () => {
        const { actions, minPrice } = this.props;
        let { maxPrice } = this.props;
        const maxVal = this.maxInput && parseFloat(this.maxInput.value, 10.0);
        const minVal = this.minInput && parseFloat(this.minInput.value, 10.0);
        maxPrice = this.isDiscountRange ? 99 : maxPrice;
        let errorDescription = '';
        let isMaxError = this.maxInput && !this.isValidCurrency(this.maxInput.value);
        let isMinError = this.minInput && !this.isValidCurrency(this.minInput.value);

        if (!isMaxError) isMaxError = this.isMaxEntered && (maxVal < minVal || maxVal > maxPrice);
        if (!isMinError) isMinError = this.isMinEntered && (minVal > maxVal || minVal < minPrice);

        if (isMaxError || isMinError) {
            errorDescription = `Please enter a range from ${minPrice} to ${maxPrice}.`;
            actions.triggerFormError([{ errorDescription }]);
        }

        this.setState({
            isMaxError,
            isMinError,
            maxMinErrorMsg: errorDescription,
            priceError: false,
        });
    };

    isValidCurrency = (currency) =>
        !!(currency.match(/^\d{0,5}(\.\d{1,2})?$/) && Number(currency) > 0);

    validateMinOnChange = () => {
        this.isMaxEntered = false;
        this.isMinEntered = true;
        this.onChange();
    };

    validateMaxOnChange = () => {
        this.isMaxEntered = true;
        this.isMinEntered = false;
        this.onChange();
    };

    validateOnKeyPress = (event) => {
        const {
            key,
            target: { value = '' },
        } = event;

        const isValidKey =
            ['.', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].filter(
                (validKey) => key === validKey
            ).length > 0;

        // decimal values more than two digits not allowed
        const isValidDecimalValue =
            value.length > 1 && value.indexOf('.') !== -1 ? value.split('.')[1].length < 2 : true;

        if (!isValidDecimalValue || !isValidKey) event.preventDefault();
    };

    submitCustomPrice = (event) => {
        const { facetId, minPrice, requestUrl } = this.props;

        let { maxPrice } = this.props;

        maxPrice = this.isDiscountRange ? 99 : maxPrice;

        const { isMaxError, isMinError, maxMinErrorMsg } = this.state;

        if (isMaxError || isMinError) {
            event.preventDefault();

            return;
        }

        let priceString = null;
        const currentRange = rangeIdentifier[facetId];
        // Read mode information from API response - Possible values for mode 1 or 2
        const mode = getURLParameterBykey('mode', requestUrl);
        let valueChanged = false;

        /* istanbul ignore next */
        if (this.maxInput.value && this.minInput.value === '') {
            this.minInput.value = minPrice;
            valueChanged = true;
        }

        if (this.maxInput.value === '' && this.minInput.value) {
            this.maxInput.value = maxPrice;
            valueChanged = true;
        }

        const maxValue = Number(this.maxInput.value);
        this.maxInput.value = maxValue % 1 !== 0 ? maxValue.toFixed(2) : maxValue;

        const minValue = Number(this.minInput.value);
        this.minInput.value = minValue % 1 !== 0 ? minValue.toFixed(2) : minValue;

        priceString = `${currentRange.identifier} ${this.minInput.value} ${this.maxInput.value}`;

        const keyString = currentRange.key_identifier;

        if (
            this.maxInput.value &&
            this.minInput.value &&
            (!(maxMinErrorMsg.length > 0) || valueChanged)
        ) {
            updateUrl(
                null,
                {
                    [keyString]: priceString,
                    activeFacetId: facetId,
                    mode,
                    page: null,
                },
                null,
                {
                    action: 'add',
                    name: currentRange.name,
                    value: `$${this.minInput.value} - $${this.maxInput.value}`,
                }
            );
        } else {
            this.validateMaxOnChange();
            this.validateMinOnChange();
        }
    };

    scrollToMyRef = () => {
        const { current } = this.myRef;
        const parentNode = current && current.parentNode;

        if (parentNode) parentNode.scrollTop = parentNode.scrollHeight;
    };

    render() {
        const { minPrice, priceRangeInputSize } = this.props;
        const { errorMsg, isMaxError, isMinError, maxMinErrorMsg, priceError } = this.state;

        const hasError = errorMsg || priceError || maxMinErrorMsg.length > 0;

        const getErrorMessage = () => {
            if (errorMsg || priceError) return errorMsg;
            if (maxMinErrorMsg.length > 0) return maxMinErrorMsg;
            return '';
        };

        const errorConfig = {
            icon: {
                height: '32',
                iconName: 'ydt-error-fill',
                viewBox: '4 0 32 32',
                width: '32',
                iconComponent: <Error />,
            },
            wrapper: {
                className: 'font-open-sans normal-case text-error text-small',
            },
        };

        const priceRangeConfig = {
            common: {
                inputWrapperClasses: [
                    'border-1',
                    'border-solid',
                    'p-1',
                    'relative',
                    'rounded-medium',
                    cx('input-symbol'),
                ],
                input: {
                    id: null,
                    maxLength: '6',
                    onKeyPress: this.validateOnKeyPress,
                    size: priceRangeInputSize,
                    theme: cx({
                        'ml-2 w-full': !this.isDiscountRange,
                        'mr-2 pr-3 text-right w-full': this.isDiscountRange,
                    }),
                    type: 'text',
                    wrapperTheme: cx('price-input-wrapper', {
                        dollar: !this.isDiscountRange,
                        percentage: this.isDiscountRange,
                    }),
                },
            },
            button: {
                className: [
                    'bg-white-100',
                    'border-1',
                    'border-gray-300',
                    'border-solid',
                    'font-open-sans-semibold',
                    'no-underline',
                    'p-1',
                    'rounded-medium',
                    'text-medium',
                    'w-8',
                ].join(' '),
                onClick: this.submitCustomPrice,
            },
            max: {
                input: {
                    onChange: this.validateMaxOnChange,
                    placeholder: 'Max',
                    refCallBack: (ref) => {
                        this.maxInput = ref;
                    },
                },
            },
            min: {
                input: {
                    defaultValue: `${minPrice}`,
                    onChange: this.validateMinOnChange,
                    placeholder: 'Min',
                    refCallBack: (ref) => {
                        this.minInput = ref;
                    },
                },
            },
            wrapper: {
                className: 'flex justify-between mx-0 my-2 text-small',
            },
            get maxInputWrapper() {
                return {
                    className: [
                        this.common.inputWrapperClasses.join(' '),
                        cx({
                            'bg-error': isMaxError,
                            'bg-white-100': !isMaxError,
                            'border-error': isMaxError,
                            'border-gray-300': !isMaxError,
                        }),
                    ].join(' '),
                };
            },
            get minInputWrapper() {
                return {
                    className: [
                        this.common.inputWrapperClasses.join(' '),
                        cx({
                            'bg-error': isMinError,
                            'bg-white-100': !isMinError,
                            'border-error': isMinError,
                            'border-gray-300': !isMinError,
                        }),
                    ].join(' '),
                };
            },
        };

        return (
            <>
                <div {...priceRangeConfig.wrapper}>
                    <span {...priceRangeConfig.minInputWrapper}>
                        <Input
                            {...{
                                ...priceRangeConfig.common.input,
                                ...priceRangeConfig.min.input,
                            }}
                        />
                    </span>
                    <span className="self-center text-center w-2">to</span>
                    <span {...priceRangeConfig.maxInputWrapper}>
                        <Input
                            {...{
                                ...priceRangeConfig.common.input,
                                ...priceRangeConfig.max.input,
                            }}
                        />
                    </span>
                    <button {...priceRangeConfig.button} type="button">
                        Apply
                    </button>
                </div>
                <div ref={this.myRef}>
                    {hasError && (
                        <div {...errorConfig.wrapper}>
                            <Icon {...errorConfig.icon}>{errorConfig.icon.iconComponent}</Icon>
                            {getErrorMessage()}
                        </div>
                    )}
                </div>
            </>
        );
    }
}

const mapStateToProps = ({
    commonData: { requestUrl = '' } = {},
    context: {
        preferences: {
            customMaxPriceFilterValue: maxPrice = 99999,
            customMinPriceFilterValue: minPrice = 1,
            priceRangeInputSize = 5,
        } = {},
    } = {},
    noProducts: { nullResultsMessage: noProducts = '' } = {},
}) => ({
    maxPrice,
    minPrice,
    noProducts,
    priceRangeInputSize,
    requestUrl,
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ triggerFormError: AnalyticsAction.triggerFormError }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, null, { pure: false })(
    CustomPriceRange
);
